import { useMemo } from "react";
import { useOrgSocialActor } from "@/scripts/OrgSocialActorContext";
import { socialThreadHref } from "@/lib/permalinks";

/** Canonical workplace Circa Social base: /orgs/:orgId/social */
export function orgSocialBasePath(orgId) {
  if (!orgId) return null;
  return `/orgs/${encodeURIComponent(String(orgId))}/social`;
}

/**
 * Social plane path builders. When inside OrgSocialActorProvider, paths stay
 * nested under /orgs/:orgId/social/* so workplace Circa Social nav stays active.
 */
export function useSocialPaths() {
  const actor = useOrgSocialActor();
  const basePath = actor?.basePath || null;
  const actorOrgId = actor?.orgId || null;

  return useMemo(() => {
    const prefix = basePath ? String(basePath).replace(/\/$/, "") : null;

    return {
      basePath: prefix,
      actorOrgId,
      isOrgActor: Boolean(prefix),
      home: () => prefix || "/dashboard",
      explore: () => (prefix ? `${prefix}/explore` : "/explore"),
      search: () => (prefix ? `${prefix}/search` : "/search"),
      messages: (conversationId) => {
        if (prefix) {
          return conversationId != null
            ? `${prefix}/messages/${encodeURIComponent(String(conversationId))}`
            : `${prefix}/messages`;
        }
        return conversationId != null
          ? `/messages/${encodeURIComponent(String(conversationId))}`
          : "/messages";
      },
      profileEdit: () => (prefix ? `${prefix}/profile` : "/profile"),
      user: (handle) => {
        if (!handle) return null;
        const h = encodeURIComponent(String(handle));
        return prefix ? `${prefix}/u/${h}` : `/u/${h}`;
      },
      org: (handle) => {
        if (!handle) return null;
        const h = encodeURIComponent(String(handle));
        return prefix ? `${prefix}/org/${h}` : `/org/${h}`;
      },
      community: (id) => {
        if (id == null) return null;
        const c = encodeURIComponent(String(id));
        return prefix ? `${prefix}/c/${c}` : `/c/${c}`;
      },
      thread: ({ postId, returnPath } = {}) =>
        socialThreadHref({
          postId,
          returnPath,
          orgId: actorOrgId,
          basePath: prefix,
        }),
    };
  }, [basePath, actorOrgId]);
}
